import type { FlowEmail, FlowSummary } from '@/lib/api/email-flows/types';
import { formatCurrency } from '@/lib/currency';
import { formatDateShort } from '@/lib/date';
import { formatNumber } from '@/lib/helpers';
import type { EmailAnalytics } from './FlowEmailTimeline.types';
import { FLOW_TRIGGER } from '@/lib';

type FlowTrigger = (typeof FLOW_TRIGGER)[keyof typeof FLOW_TRIGGER];

/**
 * Calculate analytics for an email
 */
export function calculateEmailAnalytics(email: FlowEmail, currency: string): EmailAnalytics {
    if (!email.analytics || email.analytics.sent === undefined) {
        return {
            sales: '-',
            sent: '-',
            openRate: '-',
            clickRate: '-',
        };
    }

    if (email.analytics.sent === 0) {
        return {
            sales: formatCurrency(0, currency, 'en-US', 0, true),
            sent: '0',
            openRate: '0.0% (0)',
            clickRate: '0.0% (0)',
        };
    }

    const openPercentage = formatNumber(((email.analytics.opened || 0) / email.analytics.sent) * 100, {
        decimals: 2,
    });
    const clickPercentage = formatNumber(((email.analytics.clicked || 0) / email.analytics.sent) * 100, {
        decimals: 2,
    });

    return {
        sales: formatCurrency(email.analytics.revenue || 0, currency, 'en-US', 0, true),
        sent: email.analytics.sent.toLocaleString(),
        openRate: `${openPercentage}% (${email.analytics.opened || 0})`,
        clickRate: `${clickPercentage}% (${email.analytics.clicked || 0})`,
    };
}

/**
 * Determine if adding more emails should be blocked
 * @param flowTrigger - The flow trigger type
 * @param emailCount - Number of emails in the flow
 * @param flow - The full flow object (optional, needed for time-based flows)
 * @returns True if adding emails should be blocked
 */
export function shouldBlockAddingEmail(flowTrigger: FlowTrigger, emailCount: number, flow?: FlowSummary): boolean {
    // Block adding emails for cart-abandonment flows with 2 or more emails
    if (flowTrigger === 'cart-abandonment' && emailCount >= 2) {
        return true;
    }

    // Block adding emails for time-based flows that have already started
    if (flowTrigger === 'time' && flow?.data?.started === true) {
        return true;
    }

    return false;
}

/**
 * Get the scheduled time for a flow
 */
export function getFlowScheduledTime(flow: FlowSummary): string | undefined {
    if (flow.scheduled_timestamp) {
        return formatDateShort(flow.scheduled_timestamp, true, true);
    }
    if (flow.sent_timestamp) {
        return formatDateShort(flow.sent_timestamp, true, true);
    }
    return undefined;
}

/**
 * Get the trigger text for display
 * @param flowTrigger - The flow trigger type
 * @param flowScheduledTime - The scheduled time (short format)
 * @param hasNewReferralTag - Whether the flow has new_referral tag
 * @param compact - Whether this is for compact mode (email editor)
 * @param flow - The full flow object (needed for detailed time info in compact mode)
 */
export function getTriggerText(
    flowTrigger: FlowTrigger,
    flowScheduledTime: string | undefined,
    hasNewReferralTag: boolean,
    compact: boolean = false,
    flow?: FlowSummary
): string {
    if (hasNewReferralTag) return 'Sign Up';

    if (flowTrigger === 'time') {
        // In compact mode (email editor) - detailed format
        if (compact && flow?.data?.specific_time) {
            const { date, time, timezone } = flow.data.specific_time;
            if (date && time && timezone) {
                const flowTriggerDate = new Date(Date.parse(date));
                const triggerDate = new Date(
                    flowTriggerDate.getUTCFullYear(),
                    flowTriggerDate.getUTCMonth(),
                    flowTriggerDate.getUTCDate(),
                    flowTriggerDate.getUTCHours(),
                    flowTriggerDate.getUTCMinutes()
                );
                const prefix = flow.data.started ? 'Sent on' : 'Scheduled for';
                const formattedDate = formatDateShort(triggerDate, true);
                return `${prefix} ${formattedDate} at ${time} (${timezone})`;
            }
        }
        // In regular mode (flows tab) - simple format
        return `Time @ ${flowScheduledTime || 'scheduled'}`;
    }

    if (flowTrigger === 'checkout') return 'Purchase';
    if (flowTrigger === 'cart-abandonment') return 'Cart Abandonment';
    return 'Purchase';
}
